import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle, Clock, AlertCircle, RefreshCw, Music2 } from 'lucide-react';
import BandLabStudio from '../BandLabStudio';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const map = {
    queued:     { c: 'bg-amber-500/15 text-amber-300', t: 'En cola' },
    processing: { c: 'bg-blue-500/15 text-blue-300', t: 'Procesando' },
    completed:  { c: 'bg-green-500/15 text-green-300', t: 'Listo' },
    failed:     { c: 'bg-red-500/15 text-red-300', t: 'Falló' },
  };
  const m = map[status] || { c: 'bg-white/10 text-white/60', t: status || '?' };
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${m.c}`}>{m.t}</span>;
};

export default function BulkMasteringPanel({ userRole }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [publishingAllJobId, setPublishingAllJobId] = useState(null);

  const loadJobs = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API_URL}/api/mastering-og/admin-bulk/jobs`, { withCredentials: true });
      setJobs(r.data || []);
    } catch (e) { /* ignore */ } finally { setRefreshing(false); }
  };
  useEffect(() => { loadJobs(); }, []);
  useEffect(() => {
    const has = jobs.some(j => (j.tracks || []).some(t => t.status === 'queued' || t.status === 'processing' ||
      Object.values(t.variants || {}).some(v => v?.status === 'processing')));
    if (!has) return;
    const id = setInterval(loadJobs, 4000);
    return () => clearInterval(id);
  }, [jobs]);

  // Drop / select → upload immediately. Backend enqueues all 8 presets per file.
  const handleFiles = async (incoming) => {
    const arr = Array.from(incoming);
    const audios = arr.filter(f => /\.(mp3|wav|flac)$/i.test(f.name));
    if (audios.length !== arr.length) {
      toast.error(`${arr.length - audios.length} archivo(s) ignorados (formato no soportado)`);
    }
    if (audios.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      audios.forEach(f => fd.append('files', f));
      // No defaults — title falls back to filename, genre/price filled at publish time
      fd.append('default_preset', 'universal');
      fd.append('default_price', '0');
      fd.append('auto_publish', 'false');
      fd.append('metadata_json', JSON.stringify(audios.map(f => ({
        title: f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      }))));
      const r = await axios.post(`${API_URL}/api/mastering-og/admin-bulk`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${r.data.accepted} canciones subidas — masterizando los 8 presets en paralelo…`);
      setExpandedJobId(r.data.job_id);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir');
    } finally { setBusy(false); }
  };

  const handlePublishAll = async (jobId) => {
    setPublishingAllJobId(jobId);
    try {
      const r = await axios.post(`${API_URL}/api/mastering-og/admin-bulk/${jobId}/publish-all`, {}, { withCredentials: true });
      toast.success(`${r.data.count} tracks publicados`);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error publicando todos');
    } finally { setPublishingAllJobId(null); }
  };

  return (
    <div className="space-y-6" data-testid="bulk-mastering-panel">
      {/* Just the drop zone — masters all 8 presets automatically */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E8720C]/15 flex items-center justify-center">
              <Music2 className="w-5 h-5 text-[#E8720C]" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Bulk Mastering Studio</h3>
              <p className="text-xs text-white/50">Sube tus MP3 y se masterizan en los 8 presets automáticamente. Tú eliges luego cuál subes a venta.</p>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-green-400 font-bold">
            {userRole === 'admin' ? '✓ Sin restricciones' : '⏳ Requiere aprobación admin'}
          </div>
        </div>

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          className={`border-2 border-dashed ${busy ? 'border-[#E8720C]/60 bg-[#E8720C]/5' : 'border-white/15 hover:border-[#E8720C]/50'} rounded-xl p-12 text-center cursor-pointer transition-colors`}
          data-testid="bulk-dropzone"
        >
          {busy ? (
            <>
              <Loader2 className="w-8 h-8 text-[#E8720C] mx-auto mb-3 animate-spin" />
              <p className="text-sm font-medium">Subiendo y encolando 8 presets por canción…</p>
            </>
          ) : (
            <>
              <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
              <p className="text-base font-medium">Arrastra MP3/WAV/FLAC aquí o haz click</p>
              <p className="text-xs text-white/40 mt-2">Sin límite de cantidad — cada canción se masteriza en los 8 presets en paralelo</p>
            </>
          )}
          <input ref={fileInputRef} type="file" accept=".mp3,.wav,.flac" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        </div>
      </div>

      {/* Recent batches */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold">Lotes recientes</h4>
          <button onClick={loadJobs} disabled={refreshing} className="p-2 hover:bg-white/5 rounded">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {jobs.length === 0 ? (
          <p className="text-xs text-white/40 text-center py-6">Aún no hay lotes. Sube algunos MP3 arriba.</p>
        ) : (
          <div className="space-y-3" data-testid="bulk-jobs-list">
            {jobs.map(j => {
              const completedUnpublished = (j.tracks || []).filter(t => t.status === 'completed' && !t.published_track_id);
              const isExpanded = expandedJobId === j.id;
              return (
                <div key={j.id} className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden">
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        {j.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                         j.status === 'failed' ? <AlertCircle className="w-4 h-4 text-red-400" /> :
                         <Clock className="w-4 h-4 text-amber-400" />}
                        <span className="font-medium">{j.user_name || j.user_email || 'Staff'}</span>
                        <span className="text-white/40 text-xs">·</span>
                        <span className="text-white/60 text-xs">{new Date(j.created_at).toLocaleString('es')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/60">{j.completed_count}/{j.total_tracks}</span>
                        <StatusBadge status={j.status} />
                        <button onClick={() => setExpandedJobId(isExpanded ? null : j.id)} className="text-xs text-white/60 hover:text-white px-2 py-1 rounded bg-white/5">
                          {isExpanded ? 'Ocultar' : 'Abrir studio'}
                        </button>
                      </div>
                    </div>

                    {isExpanded && completedUnpublished.length > 1 && (
                      <button
                        onClick={() => handlePublishAll(j.id)}
                        disabled={publishingAllJobId === j.id}
                        className="w-full mt-2 mb-2 py-2 bg-[#E8720C]/20 hover:bg-[#E8720C]/30 border border-[#E8720C]/40 rounded text-xs font-bold inline-flex items-center justify-center gap-2"
                        data-testid={`publish-all-btn-${j.id}`}
                      >
                        {publishingAllJobId === j.id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publicando…</> : <>✓ Publicar todos los {completedUnpublished.length} sin publicar</>}
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="border-t border-white/5 p-3">
                      <BandLabStudio job={j} onPublished={loadJobs} onRefresh={loadJobs} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
